import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';

import { devLog } from '../utils/devLog';
/**
 * Auto-Save Hook - Prevents data loss on browser refresh
 *
 * Features:
 * - Saves draft every 2 seconds after last change
 * - Loads saved draft on mount
 * - Shows save status indicator
 * - Deletes draft after publish
 *
 * @param {number} tempNo - Template number (1-20)
 * @param {object} formData - Complete template configuration
 * @param {boolean} enabled - Enable/disable auto-save
 * @returns {object} - { saveStatus, lastSaved, loadDraft, deleteDraft, saveDraft }
 */
export const useAutoSave = (tempNo, formData, enabled = true) => {
    const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
    const [lastSaved, setLastSaved] = useState(null);
    const saveTimeoutRef = useRef(null);
    const hasDraftRef = useRef(false);
    const isFirstRender = useRef(true);

    // Auto-save function
    const saveDraft = useCallback(async () => {
        if (!enabled || !tempNo || !formData) return;

        // Skip save on first render (before any changes)
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        try {
            setSaveStatus('saving');

            const response = await axios.post(
                '/api/template-drafts/save/',
                {
                    temp_no: tempNo,
                    draft_data: formData
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': Cookies.get('csrftoken')
                    }
                }
            );

            if (response.data.success) {
                setSaveStatus('saved');
                setLastSaved(new Date());
                hasDraftRef.current = true;

                // Reset status after 3 seconds
                setTimeout(() => {
                    setSaveStatus('idle');
                }, 3000);
            }
        } catch (error) {
            devLog.error('Auto-save error:', error);
            setSaveStatus('error');

            // Reset error status after 5 seconds
            setTimeout(() => {
                setSaveStatus('idle');
            }, 5000);
        }
    }, [tempNo, formData, enabled]);

    // Debounced auto-save (saves 2 seconds after last change)
    useEffect(() => {
        if (!enabled || isFirstRender.current) return;

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            saveDraft();
        }, 2000); // Save 2 seconds after last change

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [formData, saveDraft, enabled]);

    // Load draft on mount
    const loadDraft = useCallback(async () => {
        if (!tempNo) return null;

        try {
            const response = await axios.get(
                `/api/template-drafts/load/${tempNo}/`,
                {
                    headers: {
                        'X-CSRFToken': Cookies.get('csrftoken')
                    }
                }
            );

            if (response.data.success && response.data.has_draft) {
                hasDraftRef.current = true;
                return response.data.draft;
            }
            return null;
        } catch (error) {
            devLog.error('Error loading draft:', error);
            return null;
        }
    }, [tempNo]);

    // Delete draft after successful save/publish
    const deleteDraft = useCallback(async () => {
        if (!tempNo) return;

        try {
            await axios.delete(
                `/api/template-drafts/delete/${tempNo}/`,
                {
                    headers: {
                        'X-CSRFToken': Cookies.get('csrftoken')
                    }
                }
            );
            hasDraftRef.current = false;
            setSaveStatus('idle');
            setLastSaved(null);
        } catch (error) {
            devLog.error('Error deleting draft:', error);
        }
    }, [tempNo]);

    return {
        saveStatus,
        lastSaved,
        loadDraft,
        deleteDraft,
        saveDraft,
        hasDraft: hasDraftRef.current
    };
};

export default useAutoSave;
